import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { InvoiceNavigation } from "@/components/nabava/InvoiceNavigation";
import { GuidedCaseLayout } from "@/components/radnici/GuidedCaseLayout";
import { errorMessage } from "@/lib/api";
import { expensePath, expenseReceipts, refundLabels, type ExpenseDetail } from "@/lib/expenseReceipts";
import { hasProcurement } from "@/lib/procurement";
import { hasInvoicePermission, invoiceMoney } from "@/lib/receivedInvoices";
import "@/components/nabava/ReceivedInvoices.css";

export function ExpenseWithoutOrderDetailPage() {
  const { id = "" } = useParams(), { user } = useAuth(), navigate = useNavigate();
  const allowed = hasInvoicePermission(user) || hasProcurement(user, "create");
  const [loadedExpense, setExpense] = useState<ExpenseDetail | null>(null);
  const expense = loadedExpense?.id === id ? loadedExpense : null;
  const [error, setError] = useState(""), [photoError, setPhotoError] = useState("");
  const [photo, setPhoto] = useState<{ id: string; url: string } | null>(null), [reload, setReload] = useState(0);
  const [confirm, setConfirm] = useState(false), [saving, setSaving] = useState(false);
  const sending = useRef(false);
  useEffect(() => {
    if (!allowed) return;
    const abort = new AbortController();
    let objectUrl = "";
    expenseReceipts.detail(id, abort.signal).then(async (data) => {
      if (abort.signal.aborted) return;
      setExpense(data); setError(""); setPhotoError("");
      try {
        const blob = await expenseReceipts.photo(id, abort.signal);
        if (!abort.signal.aborted) { objectUrl = URL.createObjectURL(blob); setPhoto({ id, url: objectUrl }); }
      } catch (cause) {
        if (!abort.signal.aborted) setPhotoError(errorMessage(cause, "Fotografija nije dostupna."));
      }
    }).catch((cause) => {
      if (!abort.signal.aborted) setError(errorMessage(cause, "Trošak nije učitan."));
    });
    return () => { abort.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [allowed, id, reload]);
  async function markPaid() {
    if (!expense || sending.current || !hasInvoicePermission(user, "likvidiraj")) return;
    sending.current = true; setSaving(true); setError("");
    try {
      setExpense(await expenseReceipts.markRefundPaid(expense.id, expense.version));
      setConfirm(false);
    } catch (cause) {
      setError(errorMessage(cause, "Povrat nije označen kao isplaćen. Osvježite trošak i pokušajte ponovno."));
      setConfirm(false);
    } finally { sending.current = false; setSaving(false); }
  }
  const pending = expense?.stanje_povrata === "za_povrat";
  const canPay = pending && hasInvoicePermission(user, "likvidiraj");
  const action = canPay ? <Button disabled={saving} onClick={() => setConfirm(true)}>Povrat isplaćen</Button> : undefined;
  return <div className="invoice-screen space-y-4 py-4">
    <InvoiceNavigation />
    <Button variant="secondary" onClick={() => navigate(expensePath)}>Natrag na popis troškova</Button>
    {!allowed ? <p role="alert">Nemate ovlast pregleda troškova bez narudžbe.</p> : <>
      {error && <div><p role="alert">{error}</p><Button variant="secondary" disabled={saving} onClick={() => { setExpense(null); setPhoto(null); setConfirm(false); setReload((value) => value + 1); }}>Osvježi trošak</Button></div>}
      {!expense ? !error && <p role="status">Učitavam trošak…</p> : <GuidedCaseLayout
        number={expense.datum} kind="Bez narudžbe" person={expense.trgovina} owner={expense.unio}
        summary={refundLabels[expense.stanje_povrata]} tone={pending ? "warn" : "good"} signals={[]}
        steps={[
          { id: "saved", name: "Trošak spremljen", state: "completed", closedBy: expense.unio, closedOn: expense.datum },
          { id: "refund", name: "Povrat radniku", state: pending ? "current" : "completed", description: refundLabels[expense.stanje_povrata] },
        ]}
        checklist={pending ? [{ id: "recipient", label: "Primatelj povrata evidentiran", ready: !!expense.primatelj_povrata, detail: expense.primatelj_povrata || "", actions: canPay ? <Button variant="secondary" disabled={saving} onClick={() => setConfirm(true)}>Evidentiraj isplatu</Button> : undefined }] : []}
        instructions={pending ? "Nakon zasebne isplate radniku evidentirajte povrat. Povrat se ne obračunava kroz plaću." : undefined}
        primaryAction={confirm ? undefined : action} stepOrder="sequential" omitMissingAttribution metadataOpen
        currentContent={confirm && canPay ? <div role="dialog" aria-label="Potvrda isplate povrata" className="space-y-3 rounded-lg border p-4">
          <p>Potvrđujete da je {invoiceMoney(expense.bruto)} isplaćeno osobi {expense.primatelj_povrata}?</p>
          <div className="flex flex-wrap gap-2">
            <Button autoFocus disabled={saving} onClick={() => void markPaid()}>{saving ? "Spremam…" : "Potvrdi isplatu"}</Button>
            <Button variant="secondary" disabled={saving} onClick={() => setConfirm(false)}>Odustani</Button>
          </div>
        </div> : undefined}
        metadata={<dl className="invoice-facts">
          <dt>Datum</dt><dd>{expense.datum}</dd><dt>Gradilište</dt><dd>{expense.gradiliste}</dd>
          <dt>Neto</dt><dd>{invoiceMoney(expense.neto)}</dd><dt>PDV</dt><dd>{invoiceMoney(expense.pdv)}</dd>
          <dt>Bruto</dt><dd>{invoiceMoney(expense.bruto)}</dd><dt>Primatelj povrata</dt><dd>{expense.primatelj_povrata || "Povrat nije potreban"}</dd>
          <dt>Unio</dt><dd>{expense.unio}</dd><dt>Opis</dt><dd>{expense.opis}</dd>
        </dl>}
        diary={<ul className="space-y-2 text-sm"><li>{expense.datum} · Trošak unio: {expense.unio}.</li>
          {expense.povrat_isplacen_at && <li>{new Date(expense.povrat_isplacen_at).toLocaleString("hr-HR")} · Povrat označen kao isplaćen.</li>}
        </ul>}
      ><section className="space-y-3 rounded-xl border p-4"><h2 className="text-lg font-semibold">Fotografija računa</h2>
        {photoError ? <p role="alert">{photoError}</p> : photo?.id === id ? <img src={photo.url} alt="Fotografija računa" className="max-w-full rounded-lg" /> : <p role="status">Učitavam fotografiju…</p>}
      </section></GuidedCaseLayout>}
    </>}
  </div>;
}
